import { ref } from "vue";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfig) => void;
          prompt: (callback?: (notification: PromptMomentNotification) => void) => void;
          renderButton: (element: HTMLElement, config: GsiButtonConfiguration) => void;
          cancel: () => void;
        };
      };
    };
    GOOGLE_CLIENT_ID?: string;
  }
}

interface GoogleIdConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface PromptMomentNotification {
  isDisplayed: () => boolean;
  isNotDisplayed: () => boolean;
  getNotDisplayedReason: () => string;
  isSkippedMoment: () => boolean;
  getSkippedReason: () => string;
  isDismissedMoment: () => boolean;
  getDismissedReason: () => string;
}

interface GsiButtonConfiguration {
  type?: "standard" | "icon";
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "large" | "medium" | "small";
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
  shape?: "rectangular" | "pill" | "circle" | "square";
  logo_alignment?: "left" | "center";
  width?: number;
  locale?: string;
}

export interface GoogleUserData {
  googleId: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  picture: string;
  token: string;
}

function decodeJWT(token: string): Record<string, unknown> {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return JSON.parse(jsonPayload);
}

export function useGoogleSignIn() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isInitialized = ref(false);

  const initializeGoogleSignIn = (onSuccess: (userData: GoogleUserData) => void, onError?: (err: string) => void) => {
    if (!window.google) {
      error.value = "Google Sign-In SDK not loaded";
      onError?.("Google Sign-In SDK not loaded");
      return;
    }

    const clientId = window.GOOGLE_CLIENT_ID;
    if (!clientId) {
      error.value = "Google Client ID not configured";
      onError?.("Google Client ID not configured");
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response: GoogleCredentialResponse) => {
        isLoading.value = true;
        error.value = null;

        try {
          const payload = decodeJWT(response.credential);
          const userData: GoogleUserData = {
            googleId: payload.sub as string,
            email: payload.email as string,
            name: payload.name as string,
            firstName: payload.given_name as string || "",
            lastName: payload.family_name as string || "",
            picture: payload.picture as string || "",
            token: response.credential,
          };
          onSuccess(userData);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Failed to decode Google token";
          error.value = errorMessage;
          onError?.(errorMessage);
        } finally {
          isLoading.value = false;
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    isInitialized.value = true;
  };

  const renderGoogleButton = (elementId: string, options?: Partial<GsiButtonConfiguration>) => {
    if (!window.google || !isInitialized.value) {
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      return;
    }

    window.google.accounts.id.renderButton(element, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
      shape: "rectangular",
      width: 280,
      ...options,
    });
  };

  const promptGoogleSignIn = () => {
    if (!window.google || !isInitialized.value) {
      return;
    }

    window.google.accounts.id.prompt();
  };

  return {
    isLoading,
    error,
    isInitialized,
    initializeGoogleSignIn,
    renderGoogleButton,
    promptGoogleSignIn,
  };
}
